/**
 * AURALA: EMAIL CAPTURE MODAL
 * Handles email capture modal open/close and form submission.
 * Stores customers in Shopify's customer section via native customer form.
 * Translated from src/components/EmailCaptureModal.tsx
 */

(function() {
  'use strict';

  const modal = document.querySelector('.email-capture-modal');
  if (!modal) return;

  const backdrop = modal.querySelector('.email-capture-backdrop');
  const closeButton = modal.querySelector('.email-capture-close');
  const form = modal.querySelector('form');
  const formState = modal.querySelector('.email-capture-form');
  const successState = modal.querySelector('.email-capture-success');
  const errorDiv = modal.querySelector('.email-capture-error');
  const emailInput = modal.querySelector('#EmailCaptureEmail');
  const submitButton = form ? form.querySelector('button[type="submit"]') : null;
  const submitText = submitButton ? submitButton.querySelector('.submit-text') : null;
  const submittingText = submitButton ? submitButton.querySelector('.submitting-text') : null;

  const CONFIG = window.AuralaNewsletterPopup || {};
  const STORAGE_KEY = 'aurala_newsletter_popup_shown';
  let hasTriggered = false;
  let previousBodyOverflow = '';

  function getPageType() {
    const cls = (document.body && document.body.className) || '';
    const m = cls.match(/template-([a-z0-9_-]+)/i);
    return m ? m[1] : '';
  }

  function shouldRunOnThisPage() {
    const pageRule = String(CONFIG.pages || 'all');
    const pageType = getPageType();
    if (pageRule === 'all') return true;
    if (pageRule === 'home') return pageType === 'index';
    if (pageRule === 'product') return pageType === 'product';
    if (pageRule === 'collection') return pageType === 'collection';
    if (pageRule === 'page') return pageType === 'page';
    return true;
  }

  function isTouchDevice() {
    return Boolean(('ontouchstart' in window) || (navigator && navigator.maxTouchPoints > 0));
  }

  function wasRecentlyShown() {
    const days = Number(CONFIG.frequencyDays);
    if (!Number.isFinite(days) || days <= 0) return false;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return false;
      const shownDate = new Date(stored);
      const now = new Date();
      const daysDiff = (now.getTime() - shownDate.getTime()) / (1000 * 60 * 60 * 24);
      return daysDiff < days;
    } catch (e) {
      return false;
    }
  }

  function markAsShown() {
    try {
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch (e) {}
  }

  function openModal() {
    if (modal.classList.contains('hidden')) {
      markAsShown();
    }
    modal.classList.remove('hidden');
    previousBodyOverflow = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';
    // Focus email input
    if (emailInput) {
      setTimeout(() => emailInput.focus(), 100);
    }
  }

  function normalizeEmail(raw) {
    if (typeof raw !== 'string') return '';
    return raw.trim().toLowerCase().replace(/\s+/g, '');
  }

  function isValidEmail(email) {
    // Keep this intentionally conservative; HTML5 validation also runs via input type=email.
    if (!email) return false;
    if (email.length > 254) return false;
    // Basic RFC-ish: local@domain.tld (no spaces)
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  async function detectShopifyCustomerFormSuccess(response) {
    // Shopify newsletter/customer form usually redirects with `customer_posted=true` on success.
    const finalUrl = String(response && response.url ? response.url : '');
    if (finalUrl.includes('customer_posted=true') || finalUrl.includes('contact_posted=true')) {
      return { ok: true };
    }

    if (finalUrl.includes('/challenge') || finalUrl.includes('captcha')) {
      return { ok: false, challengeUrl: finalUrl };
    }

    const status = response && typeof response.status === 'number' ? response.status : 0;
    if (status === 403 || status === 429) {
      // Likely bot protection; fall back to challenge HTML if present.
      // If we only have a generic status, show a standard error.
      try {
        const text = await response.text();
        if (/shopify-challenge|hcaptcha|h-captcha|recaptcha|g-recaptcha|captcha|\/challenge/i.test(text)) {
          return { ok: false, challengeHtml: text };
        }
      } catch (e) {}
      return { ok: false, message: 'Please try again. Something didn’t go through.' };
    }
    if (response && response.redirected && finalUrl && (finalUrl.includes('/challenge') || finalUrl.includes('captcha'))) {
      return { ok: false, challengeUrl: finalUrl };
    }

    // If not a success redirect, inspect HTML for error hints on the customer form.
    let text = '';
    try {
      text = await response.text();
    } catch (e) {}

    if (!text) {
      if (response && response.ok) return { ok: true };
      return { ok: false, message: 'Please try again. Something didn’t go through.' };
    }

    let doc = null;
    try {
      doc = new DOMParser().parseFromString(text, 'text/html');
    } catch (e) {}

    if (!doc) {
      if (response && response.ok) return { ok: true };
      return { ok: false, message: 'Please try again. Something didn’t go through.' };
    }

    const hasChallengeMarkup = /shopify-challenge|hcaptcha|h-captcha|recaptcha|g-recaptcha|captcha|\/challenge/i.test(text);
    if (hasChallengeMarkup) {
      return { ok: false, challengeHtml: text, challengeUrl: finalUrl.includes('/challenge') || finalUrl.includes('captcha') ? finalUrl : '' };
    }

    const successMessage = doc.querySelector('.form__message--success');
    if (successMessage) {
      return { ok: true };
    }

    const forms = Array.from(doc.querySelectorAll('form')).filter((formEl) =>
      formEl.querySelector('input[name="contact[email]"]')
    );

    if (!forms.length) {
      return { ok: Boolean(response && response.ok) };
    }

    let errorMessage = '';
    let hasInvalid = false;
    let hasSuccess = false;

    for (const formEl of forms) {
      const invalidInput = formEl.querySelector('input[name="contact[email]"][aria-invalid="true"]');
      const successEl = formEl.querySelector('.form__message--success');
      const messageEl = formEl.querySelector('.form__message, .errors, .error');

      if (successEl) {
        hasSuccess = true;
        break;
      }

      if (invalidInput || messageEl) {
        hasInvalid = true;

        if (messageEl) {
          const messageClone = messageEl.cloneNode(true);
          messageClone.querySelectorAll('svg, use, .svg-wrapper').forEach((node) => node.remove());
          errorMessage = String(messageClone.textContent || '').replace(/\s+/g, ' ').trim();
        }
        break;
      }
    }

    if (hasSuccess) {
      return { ok: true };
    }

    if (hasInvalid) {
      if (/captcha/i.test(errorMessage || '') || /missing captcha token/i.test(text)) {
        return { ok: false, requiresFullSubmit: true };
      }
      return { ok: false, message: errorMessage || 'Please check your email and try again.' };
    }

    const hasChallenge = /shopify-challenge|hcaptcha/i.test(text);
    if (hasChallenge) {
      return { ok: false, challengeHtml: text, challengeUrl: finalUrl.includes('/challenge') || finalUrl.includes('captcha') ? finalUrl : '' };
    }

    if (status === 400) {
      if (/captcha/i.test(text)) {
        return { ok: false, requiresFullSubmit: true };
      }
      return { ok: false, message: 'Please try again. Something didn’t go through.' };
    }
    return { ok: Boolean(response && response.ok) };
  }

  function closeModal() {
    modal.classList.add('hidden');
    document.body.style.overflow = previousBodyOverflow;
    // Reset form state after close
    setTimeout(() => {
      formState.classList.remove('hidden');
      successState.classList.add('hidden');
      if (form) form.reset();
      if (errorDiv) {
        errorDiv.classList.add('hidden');
        errorDiv.textContent = '';
      }
      if (submitButton) {
        submitButton.disabled = false;
      }
      if (submitText) submitText.classList.remove('hidden');
      if (submittingText) submittingText.classList.add('hidden');
    }, 300);
  }

  function maybeTrigger(reason) {
    if (!CONFIG || CONFIG.enabled === false) return;
    if (!shouldRunOnThisPage()) return;
    if (hasTriggered) return;
    if (wasRecentlyShown()) return;
    if (!CONFIG.showOnMobile && isTouchDevice()) return;
    if (!modal.classList.contains('hidden')) return;

    hasTriggered = true;
    openModal();
  }

  // Close handlers
  if (backdrop) {
    backdrop.addEventListener('click', closeModal);
  }

  if (closeButton) {
    closeButton.addEventListener('click', closeModal);
  }

  // Form submission
  if (form) {
    form.addEventListener('submit', function(e) {
      e.preventDefault();
      e.stopImmediatePropagation();

      // Normalize + validate email before submitting
      const rawEmail = emailInput ? emailInput.value : '';
      const normalized = normalizeEmail(rawEmail);
      if (emailInput) emailInput.value = normalized;

      const html5Valid = emailInput && typeof emailInput.checkValidity === 'function'
        ? emailInput.checkValidity()
        : true;

      if (!html5Valid || !isValidEmail(normalized)) {
        if (errorDiv) {
          errorDiv.textContent = 'Please enter a valid email address.';
          errorDiv.classList.remove('hidden');
        }
        if (submitButton) submitButton.disabled = false;
        if (submitText) submitText.classList.remove('hidden');
        if (submittingText) submittingText.classList.add('hidden');
        if (emailInput && typeof emailInput.focus === 'function') emailInput.focus();
        return;
      }
      
      if (submitButton) submitButton.disabled = true;
      if (submitText) submitText.classList.add('hidden');
      if (submittingText) submittingText.classList.remove('hidden');
      if (errorDiv) {
        errorDiv.classList.add('hidden');
        errorDiv.textContent = '';
      }

      // Submit natively so Shopify can run its captcha/challenge flow.
      form.submit();
    });
  }

  // Close on Escape key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && !modal.classList.contains('hidden')) {
      closeModal();
    }
  });

  // Expose openModal globally for triggering from anywhere
  window.openEmailCaptureModal = openModal;

  // Auto-trigger rules (Theme settings)
  (function initTriggers() {
    if (!CONFIG || CONFIG.enabled === false) return;
    if (!shouldRunOnThisPage()) return;

    // Exit intent (desktop)
    if (CONFIG.triggerExitIntent) {
      const threshold = Number(CONFIG.exitIntentThresholdPx);
      const enableAfter = Number(CONFIG.exitIntentEnableAfterMs);
      let exitIntentEnabled = false;

      if (!isTouchDevice() || CONFIG.showOnMobile) {
        setTimeout(() => { exitIntentEnabled = true; }, Number.isFinite(enableAfter) ? enableAfter : 0);

        document.addEventListener('mouseleave', function (e) {
          if (!exitIntentEnabled) return;
          if (!e) return;
          const y = typeof e.clientY === 'number' ? e.clientY : 9999;
          const topThreshold = Number.isFinite(threshold) ? threshold : 30;
          if (y <= topThreshold && e.relatedTarget === null) {
            maybeTrigger('exit_intent');
          }
        });
      }
    }

    // Time delay
    if (CONFIG.triggerTimeDelay) {
      const delayMs = Number(CONFIG.delayMs);
      const ms = Number.isFinite(delayMs) ? delayMs : 0;
      if (ms > 0) {
        setTimeout(() => maybeTrigger('time_delay'), ms);
      }
    }

    // Scroll depth
    if (CONFIG.triggerScroll) {
      const pct = Number(CONFIG.scrollPercent);
      const targetPct = Number.isFinite(pct) ? pct : 45;

      const onScroll = function () {
        const doc = document.documentElement;
        const scrollTop = window.scrollY || doc.scrollTop || 0;
        const scrollable = Math.max(1, (doc.scrollHeight || 1) - (window.innerHeight || 1));
        const currentPct = (scrollTop / scrollable) * 100;
        if (currentPct >= targetPct) {
          window.removeEventListener('scroll', onScroll, { passive: true });
          maybeTrigger('scroll');
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      // In case the page loads already scrolled (back/forward cache)
      setTimeout(onScroll, 150);
    }
  })();
})();
